"use client";

import { useState } from "react";
import { cn } from "../../lib/utils";
import { BigImageArea } from "../../patterns/big-image-area/big-image-area";
import { CarouselThumbnails } from "../../patterns/carousel-thumbnails/carousel-thumbnails";
import { OverlayGallery } from "../../patterns/image-gallery-overlay/overlay-gallery";
// import { ImageGalleryOverlay } from "../../patterns/image-gallery-overlay/image-gallery-overlay";
import type {
  GalleryDevice,
  GalleryImageData,
  GalleryMicrocopies,
  GalleryMouseState,
} from "./image-gallery.types";

export interface ImageGalleryProps {
  /** Array of gallery images */
  images: GalleryImageData[];

  /** Initial active image index */
  initialIndex?: number;

  /** Microcopies for all text content */
  microcopies: GalleryMicrocopies;

  /** Mouse state from Figma variant */
  mouse?: GalleryMouseState;

  /** Device type from Figma variant */
  device?: GalleryDevice;

  /** Show navigation arrows in BigImageArea */
  showNavigationArrows?: boolean;

  /** Additional CSS classes */
  className?: string;
}

export function ImageGallery(props: ImageGalleryProps) {
  // Clamp initial index to valid bounds
  const clampedInitialIndex = Math.max(
    0,
    Math.min(props.initialIndex ?? 0, (props.images?.length ?? 1) - 1)
  );
  const [activeIndex, setActiveIndex] = useState(clampedInitialIndex);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const handleImageChange = (newIndex: number) => {
    setActiveIndex(newIndex);
  };

  const handleOverlayOpen = () => {
    setIsOverlayOpen(true);
  };

  const handleOverlayClose = () => {
    setIsOverlayOpen(false);
  };

  if (!props.images || props.images.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex w-full flex-col gap-4", props.className)}>
      {/* Big Image Area */}
      <BigImageArea
        activeIndex={activeIndex}
        device={props.device}
        images={props.images}
        microcopies={{
          viewGallery: props.microcopies.viewGallery,
          previous: props.microcopies.previous,
          next: props.microcopies.next,
          imageCounter: props.microcopies.imageCounter,
        }}
        mouse={props.mouse}
        onImageChange={handleImageChange}
        onOverlayOpen={handleOverlayOpen}
        showNavigationArrows={props.showNavigationArrows}
      />

      {/* Carousel Thumbnails */}
      <CarouselThumbnails
        activeIndex={activeIndex}
        device={props.device}
        images={props.images}
        microcopies={{
          previous: props.microcopies.previous,
          next: props.microcopies.next,
        }}
        onThumbnailClick={handleImageChange}
      />
      {/* Overlay */}
      <OverlayGallery
        activeIndex={activeIndex}
        device={props.device}
        images={props.images}
        isVisible={isOverlayOpen}
        microcopies={{
          close: props.microcopies.close,
          previous: props.microcopies.previous,
          next: props.microcopies.next,
          imageCounter: props.microcopies.imageCounter,
        }}
        onClose={handleOverlayClose}
        onImageChange={handleImageChange}
      />
    </div>
  );
}
